import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, UserPlus, Loader2, CheckCircle2 } from "lucide-react";
import { useAddClient } from "../hooks";
import { ImportError } from "../api";
import { Toast } from "../components/Toast";
import { Input } from "@/shared/ui/Input";
import { DatePicker } from "@/shared/ui/DatePicker";
import type { Merchant, ManualClient } from "../types";

const EMPTY_FORM: ManualClient = {
  first_name: "",
  last_name: "",
  phone_number: "",
  pin: "",
  passport_number: "",
  birth_date: "",
  address: "",
};

type FieldKey = keyof ManualClient;

const FIELDS: { key: FieldKey; type?: "text" | "date" }[] = [
  { key: "first_name" },
  { key: "last_name" },
  { key: "phone_number" },
  { key: "pin" },
  { key: "passport_number" },
  { key: "birth_date", type: "date" },
  { key: "address" },
];

// Client-side blur/submit validation. Returns an i18n key for the message, or null when valid.
function validateField(field: FieldKey, value: string): string | null {
  const v = value.trim();
  if (!v) return "import.manualStep.validation.required";
  if (field === "phone_number" && !/^\+?\d{9,15}$/.test(v.replace(/[\s()-]/g, "")))
    return "import.manualStep.validation.phone";
  if (field === "pin" && !/^\d{14}$/.test(v)) return "import.manualStep.validation.pin";
  return null;
}

// Step 3 (manual mode) — add a single client by hand. Stays on the form after success (keep adding),
// with inline blur validation, a submit spinner, and a running "added this session" counter.
export function ManualStep({ merchant, onBack }: { merchant: Merchant; onBack: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ManualClient>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);

  const { mutate: addClientMutate, isPending } = useAddClient();

  const handleChange = useCallback(
    (field: FieldKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    [],
  );

  // Validate a single field as soon as the user leaves it.
  const handleBlur = useCallback(
    (field: FieldKey) => (e: React.FocusEvent<HTMLInputElement>) => {
      const errKey = validateField(field, e.target.value);
      setFieldErrors((prev) => ({ ...prev, [field]: errKey ? t(errKey) : undefined }));
    },
    [t],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Validate every field client-side before hitting the API.
      const nextErrors: Partial<Record<FieldKey, string>> = {};
      for (const { key } of FIELDS) {
        const errKey = validateField(key, form[key]);
        if (errKey) nextErrors[key] = t(errKey);
      }
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        return;
      }
      setFieldErrors({});
      addClientMutate(
        { merchantId: merchant.id, payload: form },
        {
          onSuccess: () => {
            setToast(
              t("import.manualStep.successToast", {
                name: `${form.first_name} ${form.last_name}`.trim(),
              }),
            );
            setForm(EMPTY_FORM);
            setAddedCount((c) => c + 1);
          },
          onError: (err) => {
            if (err instanceof ImportError && err.field) {
              setFieldErrors({ [err.field]: t(`import.errors.${err.code}`) });
            } else {
              setFieldErrors({ phone_number: t("import.errors.generic") });
            }
          },
        },
      );
    },
    [merchant.id, form, addClientMutate, t],
  );

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1.5 text-[13px] text-grey-500 hover:text-grey-700"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        {t("import.back")}
      </button>

      <h2 className="mb-1 text-[18px] font-semibold text-grey-800">{t("import.manualStep.title")}</h2>
      <p className="mb-5 text-[13px] text-grey-500">
        {merchant.first_name} {merchant.last_name}
      </p>

      {/* Running tally of clients added without leaving the form. */}
      {addedCount > 0 && (
        <div className="mb-5 flex items-center gap-2 rounded-card bg-green-bg px-4 py-2.5 text-[13px] font-medium text-green-dark">
          <CheckCircle2 size={16} strokeWidth={2} className="shrink-0" />
          {t("import.manualStep.sessionCount", { count: addedCount })}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map(({ key, type = "text" }) => (
            <div key={key} className={key === "address" ? "sm:col-span-2" : ""}>
              <label
                htmlFor={`field-${key}`}
                className="mb-1 block text-[13px] font-medium text-grey-700"
              >
                {t(`import.manualStep.${key}`)}
                <span className="ml-0.5 text-red">*</span>
              </label>
              {type === "date" ? (
                <DatePicker
                  id={`field-${key}`}
                  value={form[key]}
                  onChange={(v) => {
                    setForm((prev) => ({ ...prev, [key]: v }));
                    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
                  }}
                  placeholder={t(`import.manualStep.${key}Placeholder`)}
                  invalid={!!fieldErrors[key]}
                />
              ) : (
                <Input
                  id={`field-${key}`}
                  type={type}
                  value={form[key]}
                  onChange={handleChange(key)}
                  onBlur={handleBlur(key)}
                  placeholder={t(`import.manualStep.${key}Placeholder`)}
                  aria-invalid={fieldErrors[key] ? true : undefined}
                  invalid={!!fieldErrors[key]}
                />
              )}
              {fieldErrors[key] && <p className="mt-1 text-[12px] text-red">{fieldErrors[key]}</p>}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={isPending}
            className="flex items-center gap-2 rounded-control bg-green px-6 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95 disabled:opacity-60 disabled:active:scale-100"
          >
            {isPending ? (
              <Loader2 size={16} strokeWidth={2} className="animate-spin" />
            ) : (
              <UserPlus size={16} strokeWidth={2} />
            )}
            {isPending ? t("app.loading") : t("import.manualStep.submitBtn")}
          </button>
        </div>
      </form>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
